import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Filter,
  Search,
  ClipboardList,
  Trash2,
  X,
  CheckSquare,
  Square,
  Wifi,
  WifiOff,
  GripVertical,
  Shuffle,
  Loader2,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useWebSocket, useWebSocketEvent } from "../context/WebSocketContext";
import api, { getErrorMessage } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import TaskCard from "../components/TaskCard";
import CreateTaskModal from "../components/CreateTaskModal";

const STATUS_OPTIONS = [
  { value: "ALL", label: "All Status" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "SUPERVISOR_VERIFIED", label: "Supervisor Verified" },
  { value: "NOT_COMPLETED", label: "Not Completed" },
  { value: "VERIFIED", label: "Verified" },
];

const PRIORITY_OPTIONS = [
  { value: "ALL", label: "All Priorities" },
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
];

function SortableTaskRow({ task, onTaskUpdate, currentUser, onLongPress, selectMode, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2">
      <button
        {...attributes}
        {...listeners}
        className="mt-3 p-1 text-zinc-300 hover:text-zinc-500 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
        data-testid={`drag-handle-${task.id}`}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <div className="flex-1">
        <TaskCard
          task={task}
          onClick={onClick}
          onTaskUpdate={onTaskUpdate}
          currentUser={currentUser}
          onLongPress={onLongPress}
          selectMode={selectMode}
        />
      </div>
    </div>
  );
}

export default function TasksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isManager, user } = useAuth();
  const isSupervisor = user?.role === "SUPERVISOR";
  const { isConnected } = useWebSocket();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [isReorderMode, setIsReorderMode] = useState(false);

  // Multi-select state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);

  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const PAGE_SIZE = 6;

  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "ALL",
    category: "ALL",
    priority: "ALL",
  });

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== "ALL") params.append("status", filters.status);
      if (filters.category !== "ALL")
        params.append("category", filters.category);
      if (filters.priority !== "ALL")
        params.append("priority", filters.priority);

      params.append("limit", PAGE_SIZE);
      params.append("offset", (currentPage - 1) * PAGE_SIZE);
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasks(response.data.tasks);
      setTotalTasks(response.data.total);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    } finally {
      setLoading(false);
    }
  },[filters.status, filters.category, filters.priority, currentPage]);

  // Silent fetch (no loading state) for polling - updates both new tasks AND status changes
  const silentFetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status !== "ALL") params.append("status", filters.status);
      if (filters.category !== "ALL")
        params.append("category", filters.category);
      if (filters.priority !== "ALL")
        params.append("priority", filters.priority);

      params.append("limit", PAGE_SIZE);
      params.append("offset", (currentPage - 1) * PAGE_SIZE);
      const response = await api.get(`/tasks?${params.toString()}`);
      const newTasks = response.data.tasks;
      setTotalTasks(response.data.total);
      // Check if there are any changes (new tasks OR status changes)
      const currentTasksMap = new Map(tasks.map((t) => [t.id, t]));
      const newTasksMap = new Map(newTasks.map((t) => [t.id, t]));
      let hasChanges = false;
      // Check for new tasks
      for (const task of newTasks) {
        if (!currentTasksMap.has(task.id)) {
          hasChanges = true;
          if (task.task_type === "RECURRING") {
            toast.info(`Scheduled task now active: ${task.title}`, {
              duration: 5000,
            });
          }
          break;
        }
        // Check for status changes
        const currentTask = currentTasksMap.get(task.id);
        if (currentTask && currentTask.status !== task.status) {
          hasChanges = true;
          break;
        }
      }

      // Also check if tasks were removed
      if (!hasChanges) {
        for (const task of tasks) {
          if (!newTasksMap.has(task.id)) {
            hasChanges = true;
            break;
          }
        }
      }

      if (hasChanges) {
       setTasks(newTasks);
      }
    } catch (error) {
      console.error("Silent fetch failed:", error);
    }
  }, [filters.status, filters.category, filters.priority, tasks]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.status, filters.category, filters.priority]);

  // Fetch tasks on filter change
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get("/categories");

      const formatted = [
        { value: "ALL", label: "All Categories" },
        ...res.data.map((cat) => ({
          value: cat.name,
          label: cat.name,
        })),
      ];

      setCategories(formatted);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Polling fallback for real-time updates - check every 10 seconds
  useEffect(() => {
    const pollInterval = setInterval(() => {
      silentFetchTasks();
    }, 10000); // 10 seconds for faster status updates

    return () => clearInterval(pollInterval);
  }, [silentFetchTasks]);

  // Clear selection when exiting select mode
  useEffect(() => {
    if (!selectMode) {
      setSelectedTasks(new Set());
    }
  }, [selectMode]);

  // WebSocket event handlers for real-time updates
  const handleTaskCreated = useCallback(
    (message) => {
      const newTask = message.data;
      // Check if this task matches current filters
      const matchesFilters =
        (filters.status === "ALL" || newTask.status === filters.status) &&
        (filters.category === "ALL" || newTask.category === filters.category) &&
        (filters.priority === "ALL" || newTask.priority === filters.priority);

      // Staff only sees tasks assigned to them
      if (user?.role === "STAFF" && newTask.assigned_to !== user.id) {
        return;
      }
      // Sub-tasks are never shown as separate cards — they're embedded in parent card
      if (newTask.parent_task_id) return;
      // Supervisor: only see tasks assigned to them (excluding VERIFIED)
      if (user?.role === "SUPERVISOR") {
        if (newTask.assigned_to !== user.id || newTask.status === "VERIFIED") return;
      }
      // Staff: only see their own tasks
      if (user?.role === "STAFF" && newTask.assigned_to !== user.id) return;

      if (matchesFilters) {
        setTasks((prev) => [newTask, ...prev]);
        toast.info(`New task: ${newTask.title}`, { duration: 3000 });
      }
    },
    [filters, user],
  );

  const handleTaskUpdated = useCallback(
    (message) => {
      const updatedTask = message.data;
      setTasks((prev) => {
        // All roles: when a sub-task changes status, update the parent card's active_subtask
        // instead of treating the sub-task as its own list item
        if (updatedTask.parent_task_id) {
          return prev.map((t) => {
            if (t.id === updatedTask.parent_task_id) {
              const isStillActive = updatedTask.status !== "VERIFIED";
              return {
                ...t,
                has_active_subtask: isStillActive,
                active_subtask: isStillActive ? {
                  id: updatedTask.id,
                  status: updatedTask.status,
                  proof_photos: updatedTask.proof_photos || [],
                  rejection_reason: updatedTask.rejection_reason,
                  supervisor_verified_at: updatedTask.supervisor_verified_at,
                  assigned_to: updatedTask.assigned_to,
                  assigned_to_name: updatedTask.assigned_to_name,
                } : null,
              };
            }
            return t;
          });
        }

        const index = prev.findIndex((t) => t.id === updatedTask.id);
        if (index === -1) return prev;

        // Check if updated task still matches filters
        const matchesFilters =
          (filters.status === "ALL" || updatedTask.status === filters.status) &&
          (filters.category === "ALL" || updatedTask.category === filters.category) &&
          (filters.priority === "ALL" || updatedTask.priority === filters.priority);

        // VERIFIED tasks disappear from all roles' lists
        if (updatedTask.status === "VERIFIED") {
          return prev.filter((t) => t.id !== updatedTask.id);
        }

        if (!matchesFilters) {
          return prev.filter((t) => t.id !== updatedTask.id);
        }

        const newTasks = [...prev];
        newTasks[index] = updatedTask;
        return newTasks;
      });
    },
    [filters, user],
  );

  const handleTaskDeleted = useCallback((message) => {
    const { id } = message.data;
    setTasks((prev) => prev.filter((t) => t.id !== id));
    setSelectedTasks((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const handleTasksDeleted = useCallback((message) => {
    const { ids } = message.data;
    setTasks((prev) => prev.filter((t) => !ids.includes(t.id)));
    setSelectedTasks((prev) => {
      const newSet = new Set(prev);
      ids.forEach((id) => newSet.delete(id));
      return newSet;
    });
  }, []);

  // Handle recurring task activation (when scheduled time arrives)
  const handleRecurringTaskActivated = useCallback(
    (message) => {
      console.log("handleRecurringTaskActivated called:", message);
      const newTask = message.data;
      if (!newTask) {
        console.error("No task data in message");
        return;
      }
      // Check if already in list
      setTasks((prev) => {
        console.log(
          "Current tasks count:",
          prev.length,
          "New task:",
          newTask.id,
        );
        if (prev.some((t) => t.id === newTask.id)) {
          console.log("Task already exists in list");
          return prev; // Already exists
        }
        // Check if matches current filters
        const matchesFilters =
          (filters.status === "ALL" || newTask.status === filters.status) &&
          (filters.category === "ALL" ||
            newTask.category === filters.category) &&
          (filters.priority === "ALL" || newTask.priority === filters.priority);

        console.log("Matches filters:", matchesFilters);
        if (matchesFilters) {
          toast.info(`Scheduled task now active: ${newTask.title}`, {
            duration: 5000,
          });
          return [newTask, ...prev];
        }
        return prev;
      });
    },
    [filters],
  );

  // Subscribe to WebSocket events
  useWebSocketEvent("task_created", handleTaskCreated);
  useWebSocketEvent("task_update", handleTaskUpdated);
  useWebSocketEvent("task_deleted", handleTaskDeleted);
  useWebSocketEvent("tasks_deleted", handleTasksDeleted);
  useWebSocketEvent("recurring_task_activated", handleRecurringTaskActivated);

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  }));

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = tasks.findIndex((t) => t.id === active.id);
    const newIndex = tasks.findIndex((t) => t.id === over.id);
    const reordered = arrayMove(tasks, oldIndex, newIndex);
    setTasks(reordered);
    try {
      await api.put("/tasks/reorder", reordered.map((t, i) => ({
        id: t.id,
        sort_order: (i + 1) * 10,
      })));
    } catch {
      toast.error("Failed to save order");
      fetchTasks();
    }
  };

  useEffect(() => {
    if (isReorderMode) {
      // Load all tasks when entering reorder mode (bypass pagination)
      (async () => {
        try {
          const params = new URLSearchParams();
          if (filters.status !== "ALL") params.append("status", filters.status);
          if (filters.category !== "ALL") params.append("category", filters.category);
          if (filters.priority !== "ALL") params.append("priority", filters.priority);
          params.append("limit", 500);
          params.append("offset", 0);
          const response = await api.get(`/tasks?${params.toString()}`);
          setTasks(response.data.tasks);
        } catch {
          toast.error("Failed to load tasks for reorder");
        }
      })();
    } else {
      fetchTasks();
    }
  }, [isReorderMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredTasks = (tasks || []).filter(
    (task) =>
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === "status") {
      if (value === "ALL") {
        searchParams.delete("status");
      } else {
        searchParams.set("status", value);
      }
      setSearchParams(searchParams);
    }
  };

  // Multi-select handlers
  const toggleTaskSelection = (taskId) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const selectAllTasks = () => {
    if (selectedTasks.size === filteredTasks.length) {
      // Deselect all
      setSelectedTasks(new Set());
    } else {
      // Select all
      setSelectedTasks(new Set(filteredTasks.map((t) => t.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTasks.size === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedTasks.size} task${selectedTasks.size > 1 ? "s" : ""}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    setDeleting(true);
    try {
      await api.post("/tasks/bulk-delete", {
        task_ids: Array.from(selectedTasks),
      });
      toast.success(
        `${selectedTasks.size} task${selectedTasks.size > 1 ? "s" : ""} deleted`,
      );
      setSelectedTasks(new Set());
      setSelectMode(false);
      fetchTasks();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete tasks"));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkAutoAssign = async () => {
    if (selectedTasks.size === 0) return;
    setAutoAssigning(true);
    try {
      const response = await api.post("/tasks/bulk-auto-assign", {
        task_ids: Array.from(selectedTasks),
      });
      toast.success(response.data.message);
      setSelectedTasks(new Set());
      setSelectMode(false);
      fetchTasks();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to auto-assign tasks"));
    } finally {
      setAutoAssigning(false);
    }
  };

  const isAllSelected =
    filteredTasks.length > 0 && selectedTasks.size === filteredTasks.length;
  const isSomeSelected =
    selectedTasks.size > 0 && selectedTasks.size < filteredTasks.length;

  // Long press handler - enters select mode and selects the task
  const handleLongPress = (taskId) => {
    setSelectMode(true);
    setSelectedTasks(new Set([taskId]));
    toast.success("Selection mode activated", { duration: 1500 });
  };

  return (
    <Layout>
      <div className="space-y-4 pb-24 md:pb-6" data-testid="tasks-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-zinc-900">Tasks</h1>
            {/* Live connection indicator */}
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                isConnected
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-zinc-100 text-zinc-500"
              }`}
              title={isConnected ? "Live updates active" : "Connecting..."}
            >
              {isConnected ? (
                <Wifi className="h-3 w-3" />
              ) : (
                <WifiOff className="h-3 w-3" />
              )}
              <span className="hidden sm:inline">
                {isConnected ? "Live" : "Offline"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Select Mode Toggle - Hidden on mobile, shown on desktop (Owner/Manager/Supervisor) */}
            {(isOwner || isManager || isSupervisor) && !selectMode && (
              <Button
                variant="outline"
                onClick={() => setSelectMode(true)}
                className="h-10 px-4 rounded-full border-zinc-200 hidden md:flex"
                data-testid="enter-select-mode"
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                Select
              </Button>
            )}

            {(isOwner || isManager) && !selectMode && (
              <Button
                variant={isReorderMode ? "default" : "outline"}
                size="sm"
                onClick={() => setIsReorderMode((r) => !r)}
                className={`h-10 px-3 rounded-full gap-1 ${isReorderMode ? "bg-zinc-800 text-white" : "border-zinc-200"}`}
                data-testid="reorder-toggle-btn"
              >
                <GripVertical className="h-4 w-4" />
                {isReorderMode ? "Done" : "Reorder"}
              </Button>
            )}

            {(isOwner || isManager) && !selectMode && !isReorderMode && (
              <Button
                onClick={() => setShowCreateTask(true)}
                className="h-10 px-4 bg-[#E23744] hover:bg-[#C42B37] text-white rounded-full shadow-md"
                data-testid="create-task-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Instant Task
              </Button>
            )}
          </div>
        </div>

        {/* Long press hint for mobile - shown only when not in select mode */}
        {(isOwner || isManager) && !selectMode && (
          <p className="text-xs text-zinc-400 text-center md:hidden">
            Long press a task to select and delete
          </p>
        )}

        {/* Selection Bar - Shows when in select mode */}
        {selectMode && (
          <div
            className="flex items-center justify-between bg-zinc-100 rounded-xl p-3 animate-slide-up"
            data-testid="selection-bar"
          >
            <div className="flex items-center gap-3">
              {/* Select All Checkbox */}
              <button
                onClick={selectAllTasks}
                className="flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-900"
                data-testid="select-all-btn"
              >
                {isAllSelected ? (
                  <CheckSquare className="h-5 w-5 text-[#E23744]" />
                ) : isSomeSelected ? (
                  <div className="h-5 w-5 border-2 border-[#E23744] rounded bg-[#E23744]/20 flex items-center justify-center">
                    <div className="w-2 h-0.5 bg-[#E23744]" />
                  </div>
                ) : (
                  <Square className="h-5 w-5 text-zinc-400" />
                )}
                {isAllSelected ? "Deselect All" : "Select All"}
              </button>

              <span className="text-sm text-zinc-500">
                {selectedTasks.size} of {filteredTasks.length} selected
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Auto-assign Button */}
              <Button
                onClick={handleBulkAutoAssign}
                disabled={selectedTasks.size === 0 || autoAssigning}
                className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full disabled:opacity-50 gap-1"
                data-testid="bulk-auto-assign-btn"
              >
                {autoAssigning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Shuffle className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Auto-assign</span>
                <span>({selectedTasks.size})</span>
              </Button>

              {/* Delete Button - Owner/Manager only */}
              {(isOwner || isManager) && (
              <Button
                onClick={handleBulkDelete}
                disabled={selectedTasks.size === 0 || deleting}
                className="h-9 px-4 bg-red-500 hover:bg-red-600 text-white rounded-full disabled:opacity-50"
                data-testid="bulk-delete-btn"
              >
                {deleting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete ({selectedTasks.size})
              </Button>
              )}

              {/* Cancel Button */}
              <Button
                variant="ghost"
                onClick={() => setSelectMode(false)}
                className="h-9 px-3 rounded-full"
                data-testid="cancel-select-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-xl bg-white border-zinc-200"
              data-testid="search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-12 px-4 rounded-xl border-zinc-200 ${showFilters ? "bg-zinc-100" : ""}`}
            data-testid="filter-toggle"
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className="grid grid-cols-3 gap-2 animate-slide-up"
            data-testid="filters"
          >
            <Select
              value={filters.status}
              onValueChange={(v) => handleFilterChange("status", v)}
            >
              <SelectTrigger
                className="h-10 rounded-xl"
                data-testid="status-filter"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.category}
              onValueChange={(v) => handleFilterChange("category", v)}
            >
              <SelectTrigger
                className="h-10 rounded-xl"
                data-testid="category-filter"
              >
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.priority}
              onValueChange={(v) => handleFilterChange("priority", v)}
            >
              <SelectTrigger
                className="h-10 rounded-xl"
                data-testid="priority-filter"
              >
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Tasks List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E23744]"></div>
          </div>
        ) : filteredTasks.length > 0 ? (
          isReorderMode && (isOwner || isManager) ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={filteredTasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3" data-testid="tasks-list">
                  {filteredTasks.map((task) => (
                    <SortableTaskRow
                      key={task.id}
                      task={task}
                      onTaskUpdate={fetchTasks}
                      currentUser={user}
                      onLongPress={handleLongPress}
                      selectMode={false}
                      onClick={() => {}}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
          <div className="space-y-3" data-testid="tasks-list">
            {filteredTasks.map((task) => (
              <div key={task.id} className="flex items-start gap-3">
                {/* Checkbox for selection */}
                {selectMode && (
                  <div
                    className="pt-4 cursor-pointer"
                    onClick={() => toggleTaskSelection(task.id)}
                    data-testid={`task-checkbox-${task.id}`}
                  >
                    {selectedTasks.has(task.id) ? (
                      <CheckSquare className="h-6 w-6 text-[#E23744]" />
                    ) : (
                      <Square className="h-6 w-6 text-zinc-300 hover:text-zinc-400" />
                    )}
                  </div>
                )}

                {/* Task Card */}
                <div
                  className={`flex-1 ${selectMode ? "pointer-events-none" : ""}`}
                >
                  <TaskCard
                    task={task}
                    onClick={
                      selectMode
                        ? () => toggleTaskSelection(task.id)
                        : () => navigate(`/tasks/${task.id}`)
                    }
                    onTaskUpdate={fetchTasks}
                    currentUser={user}
                    onLongPress={handleLongPress}
                    selectMode={selectMode}
                  />
                </div>
              </div>
            ))}
          </div>
          )
        ) : (
          <Card className="bg-white rounded-2xl border border-zinc-100 shadow-sm">
            <CardContent className="p-8 text-center">
              <ClipboardList className="h-12 w-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">No tasks found</p>
              {(isOwner || isManager) && (
                <Button
                  onClick={() => setShowCreateTask(true)}
                  className="mt-4 bg-[#E23744] hover:bg-[#C42B37] text-white rounded-full"
                  data-testid="empty-create-task"
                >
                  Create Task
                </Button>
              )}
            </CardContent>
          </Card>
        )}
	
        {/* Pagination */}
        {totalTasks > PAGE_SIZE && (() => {
          const totalPages = Math.ceil(totalTasks / PAGE_SIZE);
          const getPageNumbers = () => {
            const pages = [];
            if (totalPages <= 7) {
              for (let i = 1; i <= totalPages; i++) pages.push(i);
            } else {
              pages.push(1);
              if (currentPage > 3) pages.push("...");
              for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
              }
              if (currentPage < totalPages - 2) pages.push("...");
              pages.push(totalPages);
            }
            return pages;
          };
          return (
            <div className="flex items-center justify-center gap-1 py-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-lg border border-zinc-200 text-sm disabled:opacity-40 hover:bg-zinc-50 transition-colors"
                data-testid="pagination-prev"
              >
                Previous
              </button>
              {getPageNumbers().map((page, i) =>
                page === "..." ? (
                  <span key={`ellipsis-${i}`} className="px-2 py-2 text-sm text-zinc-400">…</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === page
                        ? "bg-[#E23744] text-white"
                        : "border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                    }`}
                    data-testid={`pagination-page-${page}`}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-2 rounded-lg border border-zinc-200 text-sm disabled:opacity-40 hover:bg-zinc-50 transition-colors"
                data-testid="pagination-next"
              >
                Next
              </button>
            </div>
          );
        })()}

      </div>

      <CreateTaskModal
        open={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        onSuccess={() => {
          setShowCreateTask(false);
          const newLastPage = Math.ceil((totalTasks + 1) / PAGE_SIZE);
          setCurrentPage(newLastPage);
        }}
      />
    </Layout>
  );
}
